import { useState } from 'react';
import type { SoloRunState } from '@plotjack/core';
import { getCardById } from '@plotjack/core';

type DraftStep = 'menu' | 'add' | 'upgrade' | 'burn';

interface Props {
  state: SoloRunState;
  onDraft: (choice: 'take' | 'upgrade' | 'burn' | 'skip', id?: string) => void;
}

export function DraftWizard({ state, onDraft }: Props) {
  const [step, setStep] = useState<DraftStep>('menu');
  const deckCount = state.deck.length;
  const canAdd = state.draftOptions.length > 0;
  const upgradeable = state.hand.filter((id) => getCardById(id)?.upgradeId);
  const canUpgrade = upgradeable.length > 0;
  const canBurn = state.hand.length > 0;

  if (step === 'menu') {
    return (
      <section className="panel draft-wizard" aria-label="Between scenes deck choice">
        <p className="draft-wizard__deck">Your deck: {deckCount} cards</p>
        <h2>Between scenes</h2>
        <p className="panel-help">Pick one change, or skip and keep your deck as it is.</p>
        <div className="draft-wizard__choices">
          <button
            type="button"
            className="draft-choice-card"
            disabled={!canAdd}
            onClick={() => setStep('add')}
          >
            <span className="draft-choice-card__title">Add a card to your deck</span>
            <span className="draft-choice-card__desc">
              Draft a new page from the story. Deck grows by 1.
            </span>
            <span className="draft-choice-card__meta">
              {canAdd ? `After: ${deckCount + 1} cards` : 'No new cards offered this beat.'}
            </span>
          </button>
          <button
            type="button"
            className="draft-choice-card"
            disabled={!canUpgrade}
            onClick={() => setStep('upgrade')}
          >
            <span className="draft-choice-card__title">Upgrade a card</span>
            <span className="draft-choice-card__desc">
              Swap a card for its foil version. Same deck size, stronger effect.
            </span>
            <span className="draft-choice-card__meta">After: {deckCount} cards</span>
          </button>
          <button
            type="button"
            className="draft-choice-card"
            disabled={!canBurn}
            onClick={() => setStep('burn')}
          >
            <span className="draft-choice-card__title">Remove a card</span>
            <span className="draft-choice-card__desc">Burn a mistake and thin your deck.</span>
            <span className="draft-choice-card__meta">After: {deckCount - 1} cards</span>
          </button>
          <button type="button" className="draft-choice-card draft-choice-card--skip" onClick={() => onDraft('skip')}>
            <span className="draft-choice-card__title">Skip</span>
            <span className="draft-choice-card__desc">No changes. Move on to the next scene.</span>
            <span className="draft-choice-card__meta">After: {deckCount} cards</span>
          </button>
        </div>
      </section>
    );
  }

  if (step === 'add') {
    return (
      <section className="panel draft-wizard" aria-label="Add a card">
        <button type="button" className="btn btn-ghost" onClick={() => setStep('menu')}>
          Back
        </button>
        <p className="draft-wizard__deck">Your deck: {deckCount} cards</p>
        <h2>Add a card to your deck</h2>
        <p className="panel-help">Choose one card. It joins your deck and your hand.</p>
        <ul className="draft-pick-list">
          {state.draftOptions.map((id) => {
            const c = getCardById(id);
            return (
              <li key={id}>
                <button type="button" className="draft-pick-btn" onClick={() => onDraft('take', id)}>
                  <span className="draft-pick-btn__name">{c?.name ?? id}</span>
                  <span className="draft-pick-btn__desc">{c?.description}</span>
                  <span className="draft-pick-btn__meta">
                    Deck after: {deckCount + 1} cards · {c?.cost} Ink · {c?.beatTypes.join(', ')}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </section>
    );
  }

  if (step === 'upgrade') {
    return (
      <section className="panel draft-wizard" aria-label="Upgrade a card">
        <button type="button" className="btn btn-ghost" onClick={() => setStep('menu')}>
          Back
        </button>
        <p className="draft-wizard__deck">Your deck: {deckCount} cards</p>
        <h2>Upgrade a card</h2>
        <p className="panel-help">Pick a card to turn foil. Same number of cards, stronger page.</p>
        <ul className="draft-pick-list">
          {upgradeable.map((id) => {
            const c = getCardById(id);
            const up = c?.upgradeId ? getCardById(c.upgradeId) : null;
            return (
              <li key={id}>
                <button type="button" className="draft-pick-btn" onClick={() => onDraft('upgrade', id)}>
                  <span className="draft-pick-btn__name">{c?.name ?? id}</span>
                  <span className="draft-pick-btn__desc">
                    Becomes: {up?.name ?? c?.upgradeId} · {up?.description ?? ''}
                  </span>
                  <span className="draft-pick-btn__meta">Deck after: {deckCount} cards</span>
                </button>
              </li>
            );
          })}
        </ul>
      </section>
    );
  }

  return (
    <section className="panel draft-wizard" aria-label="Remove a card">
      <button type="button" className="btn btn-ghost" onClick={() => setStep('menu')}>
        Back
      </button>
      <p className="draft-wizard__deck">Your deck: {deckCount} cards</p>
      <h2>Remove a card</h2>
      <p className="panel-help">Pick a card to burn. It leaves your deck for good.</p>
      <ul className="draft-pick-list">
        {state.hand.map((id) => {
          const c = getCardById(id);
          return (
            <li key={id}>
              <button type="button" className="draft-pick-btn" onClick={() => onDraft('burn', id)}>
                <span className="draft-pick-btn__name">{c?.name ?? id}</span>
                <span className="draft-pick-btn__desc">{c?.description}</span>
                <span className="draft-pick-btn__meta">Deck after: {deckCount - 1} cards</span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
